import { Box, Button, Typography } from '@mui/material'
import { ReactElement, cloneElement } from 'react'

type ContentItemCardProps = {
  itemID: string
  itemName: string
  icon: ReactElement
  iconColor: string
  iconLabel: string
  buttonText: string
  buttonAction: () => void
  isLoading: boolean
  disabled?: boolean
  variant?: 'contained' | 'outlined'
  stepIndex?: number
}

export default function ContentItemCard({
  itemID,
  itemName,
  icon,
  iconColor,
  iconLabel,
  buttonText,
  buttonAction,
  isLoading,
  disabled,
  variant = 'contained',
  stepIndex,
}: ContentItemCardProps): ReactElement {
  return (
    <Box
      component="li"
      key={itemID}
      className="flex items-center justify-between p-3 border rounded"
      sx={{
        bgcolor: 'background.paper',
        borderColor: 'divider',
        opacity: disabled ? 0.5 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      <Box className="flex items-center flex-1 min-w-0 gap-3">
        <Box className="flex" sx={{ color: iconColor }} aria-label={iconLabel}>
          {cloneElement(icon, { 'aria-hidden': 'true' } as any)}
        </Box>
        <Box className="flex-1 min-w-0">
          <Typography variant="body2" className="truncate">
            {itemName}
            {stepIndex !== undefined && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: 'text.secondary' }}
                className="ml-2"
              >
                (Currently set to Step {stepIndex + 1})
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>
      <Button
        size="large"
        variant={variant}
        color="warning"
        onClick={buttonAction}
        disabled={isLoading || disabled}
        aria-label={`${buttonText}: ${itemName}`}
        aria-live="polite"
        aria-busy={isLoading}
        className="ml-2 min-w-40"
      >
        {isLoading ? 'Processing...' : buttonText}
      </Button>
    </Box>
  )
}
